import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import pollQuery from './assetBulkUploadPoll.graphql';
import { type SummaryData } from '../types';
import {
  buildSummary,
  zeroedSummary,
  type Upload,
} from '../utils/buildAssetSummary';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 100;
// A genuine not-found/ownership error yields null data every time, so stop
// after 3 consecutive null-data results; a transient error (read-replica lag,
// a one-off backend exception) clears the counter on the next good response.
const MAX_NULL_DATA_ATTEMPTS = 3;
const PROCESSING = 'PROCESSING';

export const usePollAssetBulkUpload = () => {
  const client = useApolloClient();

  const poll = useCallback(
    (
      id: string | undefined,
      isCancelled: () => boolean,
    ): Promise<SummaryData> =>
      new Promise((resolve) => {
        if (!id) {
          resolve(zeroedSummary());
          return;
        }

        let attempts = 0;
        let nullDataCount = 0;
        let latest: Upload | null = null;

        const tick = () => {
          if (isCancelled()) {
            resolve(latest ? buildSummary(latest, false) : zeroedSummary());
            return;
          }
          if (attempts >= MAX_ATTEMPTS) {
            resolve(
              latest
                ? buildSummary(latest, true)
                : { ...zeroedSummary(), stillProcessing: true },
            );
            return;
          }

          attempts += 1;

          client
            .query<AssetBulkUploadPollQuery, AssetBulkUploadPollQueryVariables>(
              {
                query: pollQuery,
                variables: { id },
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
              },
            )
            .then(({ data }) => {
              const upload = data?.assetBulkUpload;
              // Null data can mean a genuine not-found/ownership GraphQL
              // error, or a transient error (e.g. read-replica lag, a
              // one-off backend exception). Retry a bounded number of times
              // before giving up.
              if (!upload) {
                nullDataCount += 1;
                if (nullDataCount >= MAX_NULL_DATA_ATTEMPTS) {
                  resolve(zeroedSummary());
                } else {
                  setTimeout(tick, POLL_INTERVAL_MS);
                }
                return;
              }

              nullDataCount = 0;
              latest = upload;

              if (upload.status !== PROCESSING) {
                resolve(buildSummary(upload, false));
              } else {
                setTimeout(tick, POLL_INTERVAL_MS);
              }
            })
            .catch(() => setTimeout(tick, POLL_INTERVAL_MS));
        };

        tick();
      }),
    [client],
  );

  return { poll };
};
